/**
 * GameScene — 对局编排。
 * CheckersGame 是唯一权威源；本场景只做三件事：
 *  1) 把棋局状态投射为弹珠/标记的舞台状态；
 *  2) 把输入翻译为规则调用（合法性判断全部交给规则引擎）；
 *  3) 用飞行动画、音效与微震把每一次行棋变成"手感"。
 */
import * as THREE from 'three';
import { CheckersGame, type Target } from '@shared/game/rules';
import { chooseAIMove, type AIStrategy, type Difficulty } from '@shared/game/ai';
import { comboOfPath, createComboStats, recordCombo, type ComboStats } from '@shared/game/combo';
import { CAMP_POSITIONS, type PlayerCount } from '@shared/constants/camps';
import { MARBLE_RADIUS, PLAYER_COLORS } from '@shared/constants/board';
import type { GameSettings, Language, ThemePreset } from '@shared/config/theme';
import type { HexPos } from '@shared/types/game';
import { hexDistance, hexKey, parseHexKey } from '@shared/utils/hexMath';
import type {
  GameOverPayload,
  MoveRejectedPayload,
  PlayerPresencePayload,
  StateSyncPayload,
  StateUpdatePayload,
} from '@shared/net/messages';
import { TURN_WARNING_MS } from '@shared/net/constants';
import type { AppContext, Scene } from './context';
import { buildBoard, type BoardHandle } from '../core/BoardBuilder';
import { createMarble, createTargetMarker, type MarbleMesh } from '../core/MarbleFactory';
import { $settings, $theme, updateSettings } from '../store/settingsStore';
import { $matchResult } from '../store/gameStore';
import { $net } from '../store/netStore';
import { netClient } from '../net/netClient';
import { logger, netLogger } from '../debug/logger';
import { toLogErr } from '@shared/debug/logger';
import { tr, trf, type DictKey } from '../ui/i18n';
import {
  TutorialCoach,
  isTutorialDone,
  tutorialEmit,
  type CoachStep,
} from '../tutorial/TutorialCoach';

/** Delay before the AI plays (pacing, also lets the frame settle). */
const AI_DELAY_MS = 680;
/** Pause on the finished board before the result scene. */
const FINISH_DELAY_MS = 1400;

/** v4.0 组队模式：HUD 座位牌的人格紧凑标记（SCREENS §15.2 人格可读性）。 */
const STRAT_SHORT: Record<AIStrategy, DictKey> = {
  balanced: 'stratShortBalanced',
  aggressive: 'stratShortAggressive',
  defensive: 'stratShortDefensive',
  cooperative: 'stratShortCooperative',
};

interface Flight {
  m: MarbleMesh;
  from: THREE.Vector3;
  to: THREE.Vector3;
  t: number;
  dur: number;
  arc: number;
  /** v2.1 motion-streak intensity 0..1 (RULES §8.2); 0/undefined = no trail. */
  trail?: number;
  onDone: () => void;
}

/** v2.1 路径高亮特效状态（RULES §8.2：黄铜折线 + 落点脉冲环）。 */
interface PathFx {
  line: THREE.Line;
  rings: THREE.Mesh[];
  /** 落地后的淡出起始时刻（performance.now() ms）；Infinity = 常驻至落地。 */
  fadeStart: number;
}

/** v2.1 拖尾残影（运动弹珠的 ghost 副本，独立材质随寿命淡出）。 */
interface TrailGhost {
  mesh: THREE.Mesh;
  life: number;
  max: number;
  base: number;
}

export class GameScene implements Scene {
  private ctx!: AppContext;
  /** Match parameters frozen at start; live prefs (language/hints) read from store. */
  private match: GameSettings;
  private game!: CheckersGame;
  private board!: BoardHandle;
  private theme: ThemePreset;

  private marbles = new Map<string, MarbleMesh>();
  private markers: THREE.Mesh[] = [];
  private flights: Flight[] = [];

  private selectedKey: string | null = null;
  private selectedTargets: Target[] = [];
  /** hexKeys of `selectedTargets` — handed to InputManager for pick priority. */
  private selectedTargetKeys = new Set<string>();
  private hoverKey: string | null = null;
  private busy = false;
  private humanSeats = new Set<number>();
  /** v4.0 组队模式：物理座位 → AI 人格（human 座位无条目，SCREENS §15.2）。 */
  private teamPersonas = new Map<number, { strategy: AIStrategy; difficulty: Difficulty }>();

  private aiTimer: ReturnType<typeof setTimeout> | null = null;
  private finishTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubs: (() => void)[] = [];

  private moves = 0;
  private turns = 0;
  private timerLeft = 0;
  private timerShown = -1;
  private finishedGlow: { keys: Set<string>; t0: number } | null = null;

  // ── v2.1 连跳特效与计分（RULES §5.2/§8.2/§8.4）──
  /** seat → combo 累计（本地局结算用；联机由服务端随 standings 下发）。 */
  private comboStats = new Map<number, ComboStats>();
  private pathFx: PathFx | null = null;
  private ghosts: TrailGhost[] = [];
  private ghostAcc = 0;
  private comboBadgeEl: HTMLElement | null = null;
  private comboBadgeTimer: ReturnType<typeof setTimeout> | null = null;

  // ── v2.1 Tutorial 教练标记（SCREENS §12，非模态、随局销毁） ──
  private coach: TutorialCoach | null = null;

  // ── 联机模式（服务端权威，本地引擎仅作镜像） ──
  private online = false;
  private yourSeat = 0;
  private matchId: string | null = null;
  /** seat → 昵称（来自 MATCH_FOUND / STATE_SYNC 名册）。 */
  private netNames = new Map<number, string>();
  /** 当前回合的服务端时钟截止时刻（渲染倒计时用 clockOffset 换算）。 */
  private turnDeadlineAt = 0;
  /** 待重放的 STATE_UPDATE 队列（动画串行消费，慢客户端不丢帧）。 */
  private netQueue: StateUpdatePayload[] = [];
  private netPlaying = false;
  /** GAME_OVER 先于动画结束到达时暂存，队列排空后统一结算。 */
  private netGameOver: GameOverPayload | null = null;
  private netOffs: (() => void)[] = [];
  /** 已对该 deadline 做过临期警示（每回合只警示一次）。 */
  private netWarnedFor = 0;

  constructor() {
    this.match = { ...$settings.get() };
    this.theme = $theme.get();
  }

  // ── Lifecycle ───────────────────────────────────────────
  enter(ctx: AppContext): void {
    this.ctx = ctx;
    // v2.1：场景为常驻单例 —— 新局前重置 combo 记帐与特效残留。
    this.comboStats.clear();
    this.clearPathFx();
    this.clearTrailFx();
    this.clearComboBadge();
    const s = this.match;
    this.online = s.mode === 'online';
    // 场景为常驻单例：座位归属与人格每局重建（此前 humanSeats 仅联机分支
    // 清理；组队模式的任意人/AI 搭配使跨局残留成为必现问题）。
    this.humanSeats.clear();
    this.teamPersonas.clear();
    if (this.online) this.initOnlineMatch();
    else if (s.mode === 'team') this.initTeamMatch();
    else this.game = CheckersGame.create(s.playerCount as PlayerCount);
    if (s.mode !== 'team') {
      for (const seat of this.game.seats) {
        if (s.mode === 'hotseat' || seat === 0) this.humanSeats.add(seat);
      }
    }
    if (this.online) {
      this.humanSeats.clear();
      this.humanSeats.add(this.yourSeat);
    }
    logger.info('对局开始', {
      event: 'match.started',
      mode: s.mode,
      players: this.game.seats.length,
      seats: [...this.game.seats],
      difficulty: s.mode === 'ai' ? s.aiDifficulty : undefined,
      ...(this.matchId ? { matchId: this.matchId } : {}),
      yourSeat: this.online ? this.yourSeat : undefined,
    });

    this.buildStage();
    this.bindInput();
    if (this.online) this.bindNet();

    // Seat the player behind their own camp (seat 0 sits at −Z).
    // Distance 38 frames the whole 121-hole hexagram *and* its outer frame
    // (tips reach r≈14.8): the nearest tip sits at NDC≈0.85, inside the 42°
    // frustum with margin for the HUD.
    // 联机：相机落在本家营后 —— 每座位绕棋盘 60°（π/3）。
    // 组队：相机锚定回合序首位人类席位（全 AI 观战锚定 seat 0）。
    const teamAnchor =
      s.mode === 'team' && !this.online
        ? (this.game.seats.find((seat) => this.humanSeats.has(seat)) ?? 0)
        : 0;
    const azimuth = this.online
      ? Math.PI - this.yourSeat * (Math.PI / 3)
      : s.mode === 'team'
        ? Math.PI - teamAnchor * (Math.PI / 3)
        : Math.PI;
    ctx.engine.getRig().view(azimuth, 0.96, 38, true);

    this.timerLeft = s.turnTimerSec;
    this.refreshHud();
    ctx.hud.setChain(null);
    this.beginTurn();
    this.postEnter();
    this.maybeStartTutorial();
  }

  /**
   * 联机对局初始化：以 GAME_START / STATE_SYNC 快照重建镜像引擎，
   * 座位/名册/回合计时全部取自服务端。缺快照（深链/刷新/模式串话）→
   * 降级为本地热座局（ONLINE_RULES §11 契约：串话永不阻塞开局）。
   */
  private initOnlineMatch(): void {
    const net = $net.get();
    const snap = net.gameStart;
    const match = net.match;
    if (!snap || !match) {
      netLogger.warn('联机快照缺失，降级为本地对局', { event: 'match.online.missing_snapshot' });
      this.match.mode = 'hotseat';
      this.online = false;
      updateSettings({ mode: 'hotseat' });
      this.game = CheckersGame.create(this.match.playerCount as PlayerCount);
      return;
    }
    this.game = CheckersGame.restore(snap.state);
    this.matchId = snap.matchId;
    this.yourSeat = match.yourSeat;
    this.turnDeadlineAt = snap.turnDeadlineAt;
    for (const p of match.players) this.netNames.set(p.seat, p.nickname);
    netLogger.info('联机对局启动', {
      event: 'match.online.started',
      matchId: this.matchId,
      yourSeat: this.yourSeat,
      players: match.players.length,
    });
  }

  /**
   * 组队对局初始化（v4.0 · SCREENS §15 / RULES §11）：座位数由赛制决定
   * （2v2→4，3v3→6）；回合序位 i 映射物理座位 game.seats[i]，human 席位
   * 入 humanSeats，AI 席位按面板快照记录 策略×棋力 人格。快照缺位按默认
   * AI（均衡·普通）补齐——面板是配置的唯一入口，此处只做防御性归一。
   */
  private initTeamMatch(): void {
    const cfg = this.match.teamMatch;
    this.game = CheckersGame.create(cfg.format === '3v3' ? 6 : 4);
    this.game.seats.forEach((seat, i) => {
      const seatCfg = cfg.seats[i];
      if (seatCfg?.kind === 'human') this.humanSeats.add(seat);
      else {
        this.teamPersonas.set(seat, {
          strategy: seatCfg?.strategy ?? 'balanced',
          difficulty: seatCfg?.difficulty ?? 'medium',
        });
      }
    });
    logger.info('组队对局配置', {
      event: 'match.team.config',
      format: cfg.format,
      seats: [...this.game.seats],
      humans: [...this.humanSeats],
      personas: [...this.teamPersonas.entries()],
    });
  }

  update(dt: number, elapsed: number): void {
    this.board.update(dt, elapsed);
    this.updateFlights(dt);
    this.updateTrailFx(dt);
    this.updatePathFx(elapsed);
    this.updateGlow(elapsed);
    this.updateTimer(dt);
  }

  onThemeChange(theme: ThemePreset): void {
    this.theme = theme;
    // 棋局状态不灭：按当前 occupancy 重建舞台。
    this.buildStage();
    this.clearSelection();
    this.refreshHud();
  }

  exit(): void {
    if (this.aiTimer !== null) clearTimeout(this.aiTimer);
    if (this.finishTimer !== null) clearTimeout(this.finishTimer);
    for (const u of this.unsubs) u();
    this.unsubs = [];
    for (const off of this.netOffs) off();
    this.netOffs = [];
    this.netQueue = [];
    this.netPlaying = false;
    this.netGameOver = null;
    // v2.1：教程随局销毁（跳过/完成时已置标记）。
    this.coach?.stop();
    this.coach = null;
    // v2.1：清 combo 特效残留（路径高亮 / 拖尾残影 / 徽章 DOM）。
    this.clearPathFx();
    this.clearTrailFx();
    this.clearComboBadge();
    this.clearStage();
    this.ctx.engine.registerPickables([]);
    this.ctx.engine.getInput().setHandlers({});
    this.ctx.hud.setTurn(null);
    this.ctx.hud.setChain(null);
    this.ctx.hud.setPlayers([]);
    this.ctx.hud.setHint(null);
  }

  // ── 调试观测（v0.5 DebugLog） ───────────────────────────
  /** 棋局摘要：模式 / 人数 / 回合 / 落子计数（供调试面板）。 */
  debugSummary(): string {
    if (!this.game) return '—';
    const g = this.game;
    return [
      `mode=${this.match.mode}  players=${g.seats.length}  turn=${g.currentPlayer}  move#=${this.moves}`,
      `seats=[${g.seats.join(',')}]  human=[${[...this.humanSeats].join(',')}]  finished=${g.finished}`,
    ].join('\n');
  }

  /** 棋盘布局：六边形网格文本（'·' 空点，数字 = 座位号）。 */
  debugLayout(): string {
    const g = this.game;
    if (!g || !this.board) return '';
    const holes = [...this.board.holeWorld.keys()];
    const rows = new Map<number, { q: number; cell: string }[]>();
    for (const k of holes) {
      const { q, r } = parseHexKey(k);
      const seat = g.occupancy.get(k);
      const cell = seat === undefined ? '·' : String(seat);
      if (!rows.has(r)) rows.set(r, []);
      rows.get(r)?.push({ q, cell });
    }
    const rKeys = [...rows.keys()].sort((a, b) => a - b);
    const rMin = rKeys[0] ?? 0;
    return rKeys
      .map((r) => {
        const cells = (rows.get(r) ?? []).sort((a, b) => a.q - b.q);
        const indent = '  '.repeat(Math.max(0, r - rMin));
        return `${indent}${cells.map((c) => c.cell).join(' ')}`;
      })
      .join('\n');
  }

  // ── Stage ───────────────────────────────────────────────
  private buildStage(): void {
    this.clearStage();
    const scene = this.ctx.engine.getScene();

    this.board = buildBoard(this.theme);
    scene.add(this.board.group);
    this.ctx.engine.registerHoles(this.board.holeWorld, this.board.feltY);

    const iridescence = this.match.marbleIridescence;
    for (const seat of this.game.seats) {
      for (const pos of this.game.marblesOf(seat)) {
        const key = hexKey(pos);
        const m = createMarble({
          seat,
          pos,
          key,
          color: PLAYER_COLORS[seat] ?? '#E63946',
          iridescence,
        });
        this.placeMarble(m, pos);
        scene.add(m.mesh);
        this.marbles.set(key, m);
      }
    }
    this.refreshPickables();
  }

  private clearStage(): void {
    const scene = this.ctx.engine.getScene();
    this.clearMarkers();
    for (const m of this.marbles.values()) {
      scene.remove(m.mesh);
      m.material.dispose();
    }
    this.marbles.clear();
    this.flights = [];
    if (this.board) {
      scene.remove(this.board.group);
      this.board.dispose();
    }
  }

  private refreshPickables(): void {
    // Only marbles are raycastable. Target markers must never be pickable:
    // a ring drawn over/behind a marble would win the hit-test and swallow
    // the click, making the board feel unresponsive. Empty legal holes are
    // resolved by InputManager's nearest-hole snap (radius 0.5) instead.
    const list: THREE.Object3D[] = [];
    for (const m of this.marbles.values()) list.push(m.mesh);
    this.ctx.engine.registerPickables(list);
  }

  private placeMarble(m: MarbleMesh, pos: HexPos): void {
    const hole = this.board.holeWorld.get(hexKey(pos));
    if (!hole) return;
    m.mesh.position.set(hole.x, hole.y + MARBLE_RADIUS, hole.z);
  }

  /**
   * Re-home a marble in the `marbles` index (and its mesh.userData) after it
   * moves. The engine's occupancy is the source of truth; the stage index must
   * track it exactly so picking, glow and re-selection stay coherent.
   */
  private reindexMarble(from: HexPos, to: HexPos): void {
    const fromKey = hexKey(from);
    const toKey = hexKey(to);
    const m = this.marbles.get(fromKey);
    if (!m) return;
    this.marbles.delete(fromKey);
    m.key = toKey;
    m.pos = to;
    m.mesh.userData.key = toKey;
    this.marbles.set(toKey, m);
    if (this.selectedKey === fromKey) this.selectedKey = toKey;
    this.refreshPickables();
  }

  // ── Input ───────────────────────────────────────────────
  private bindInput(): void {
    this.ctx.engine.getInput().setHandlers({
      onPickMarble: (key) => this.onPickMarble(key),
      onPickHole: (pos) => this.onPickHole(pos),
      onPickEmpty: () => this.clearSelection(),
      onHoverMarble: (key) => {
        this.hoverKey = key;
        this.applyGlow();
      },
      targetKeys: () => this.selectedTargetKeys,
    });
  }

  private humanTurn(): boolean {
    return !this.busy && !this.game.finished && this.humanSeats.has(this.game.currentPlayer);
  }

  private onPickMarble(key: string): void {
    if (!this.humanTurn()) return;
    const seat = this.game.occupancy.get(key);
    if (seat === undefined || seat !== this.game.currentPlayer) {
      this.ctx.audio.playInvalid();
      return;
    }
    this.selectMarble(key);
  }

  private selectMarble(key: string): void {
    const all = this.game.legalTargets();
    const mine = all.find((st) => hexKey(st.marble) === key);
    if (!mine || mine.targets.length === 0) {
      this.ctx.audio.playInvalid();
      return;
    }
    this.selectedKey = key;
    this.selectedTargets = mine.targets;
    this.selectedTargetKeys = new Set(mine.targets.map((t) => hexKey(t.to)));
    this.showMarkers(mine.targets);
    this.applyGlow();
    this.ctx.audio.playSelect();
    this.ctx.hud.setHint(null);
    tutorialEmit('marble-selected'); // v2.1 教程第 2 步推进信号
  }

  private onPickHole(pos: HexPos): void {
    if (!this.humanTurn()) return;
    if (!this.selectedKey) return;
    const target = this.selectedTargets.find((t) => t.to.q === pos.q && t.to.r === pos.r);
    if (!target) {
      // 点到非落点：安静地取消选择（误触不惩罚）。
      this.clearSelection();
      return;
    }
    if (this.online) {
      // 联机：只发 {from,to} 意图，权威路径由服务端 BFS 重算；
      // 本地不做预测，等待 STATE_UPDATE 重放（PROTOCOL.md §5）。
      const from = parseHexKey(this.selectedKey);
      if (!netClient.sendMove(from, pos)) {
        this.ctx.hud.toast(tr('netMoveRejected', $settings.get().language));
      } else {
        tutorialEmit('destination-tapped');
      }
      this.clearSelection();
      return;
    }
    tutorialEmit('destination-tapped'); // v2.1 教程第 3 步推进信号
    this.executeMove(parseHexKey(this.selectedKey), pos, target);
  }

  private clearSelection(silent = true): void {
    const had = this.selectedKey !== null;
    this.selectedKey = null;
    this.selectedTargets = [];
    this.selectedTargetKeys = new Set();
    this.clearMarkers();
    this.applyGlow();
    if (had && !silent) this.ctx.audio.playInvalid();
  }

  // ── Markers ─────────────────────────────────────────────
  private showMarkers(targets: Target[]): void {
    this.clearMarkers();
    const seat = this.game.currentPlayer;
    const color = PLAYER_COLORS[seat] ?? '#E63946';
    const holeY = this.board.feltY;
    for (const tg of targets) {
      const marker = createTargetMarker(color, tg.type === 'hop');
      marker.userData = { kind: 'marker', pos: tg.to };
      const hole = this.board.holeWorld.get(hexKey(tg.to));
      if (!hole) continue;
      marker.position.set(hole.x, holeY + 0.02, hole.z);
      this.ctx.engine.getScene().add(marker);
      this.markers.push(marker);
    }
    this.refreshPickables();
  }

  private clearMarkers(): void {
    const scene = this.ctx.engine.getScene();
    for (const mk of this.markers) {
      scene.remove(mk);
      mk.geometry.dispose();
      (mk.material as THREE.Material).dispose();
    }
    this.markers = [];
    if (this.marbles.size > 0) this.refreshPickables();
  }

  // ── Move execution ──────────────────────────────────────
  /**
   * Animate a full turn action along `target.path` (RULES.md §4.2): a step is
   * one short glide; a hop chain flies segment by segment so multi-hop moves
   * read as one continuous journey to the chosen final landing.
   */
  private executeMove(from: HexPos, to: HexPos, target: Target): void {
    this.busy = true;
    const fromKey = hexKey(from);
    this.selectedKey = null;
    this.selectedTargets = [];
    this.selectedTargetKeys = new Set();
    this.clearMarkers();

    const m = this.marbles.get(fromKey);
    if (!m) {
      this.busy = false;
      return;
    }
    const speed = Math.max(0.25, this.match.animationSpeed);
    const waypoints: THREE.Vector3[] = [];
    for (const p of target.path) {
      const hole = this.board.holeWorld.get(hexKey(p));
      if (!hole) {
        this.busy = false;
        return;
      }
      waypoints.push(new THREE.Vector3(hole.x, hole.y + MARBLE_RADIUS, hole.z));
    }
    // Anchor the first waypoint at the marble's live mesh position so the
    // animation never snaps even if the stage and board disagree by a hair.
    waypoints[0] = m.mesh.position.clone();

    const isHop = target.type === 'hop';
    // v2.1：连跳特效 —— 路径黄铜高亮 + 弹珠拖尾（RULES §8.2）。
    const combo = comboOfPath(target.path);
    this.showPathFx(target.path, combo);
    const trail = isHop ? Math.min(1, 0.35 + combo * 0.18) : 0;
    const flySegment = (idx: number): void => {
      const a = waypoints[idx];
      const b = waypoints[idx + 1];
      if (!a || !b) {
        this.onMoveLandedFx(combo);
        this.settleMove(from, to, target);
        return;
      }
      this.flights.push({
        m,
        from: a,
        to: b,
        t: 0,
        dur: (isHop ? 0.36 : 0.26) / speed,
        arc: isHop ? 1.5 : 0.28,
        trail,
        onDone: () => {
          if (isHop) this.ctx.audio.playLandHop();
          if (idx + 1 >= waypoints.length - 1) {
            m.pos = to;
            this.board.shake(isHop ? 1.15 : 0.65);
            if (!isHop) this.ctx.audio.playLandStep();
            this.onMoveLandedFx(combo);
            this.settleMove(from, to, target);
          } else {
            flySegment(idx + 1);
          }
        },
      });
    };
    flySegment(0);
  }

  /** Rule step after the marble visually lands. */
  private settleMove(from: HexPos, to: HexPos, target: Target): void {
    // applyMove 会轮转回合 —— 走子座位必须在应用前捕获（combo 记帐用）。
    const moverSeat = this.game.currentPlayer;
    let result;
    try {
      result = this.game.applyMove(from, to);
    } catch (err) {
      // Rules engine disagreed with the stage: roll the marble back to its
      // authoritative cell instead of leaving the visuals desynced.
      logger.warn('规则引擎拒绝落子，舞台已回滚', {
        event: 'move.rejected',
        from: { q: from.q, r: from.r },
        to: { q: to.q, r: to.r },
        err: toLogErr(err),
      });
      const m = this.marbles.get(hexKey(from));
      if (m) this.placeMarble(m, from);
      this.busy = false;
      return;
    }
    this.moves++;
    this.trackCombo(moverSeat, result.path);

    // 结构化落子日志（observability §2.4：move.applied）。
    logger.debug('落子已应用', {
      event: 'move.applied',
      mode: this.match.mode,
      move: this.moves,
      seat: this.game.seats[this.game.turnIndex] ?? 0,
      from: { q: from.q, r: from.r },
      to: { q: to.q, r: to.r },
      kind: target.type === 'hop' ? (result.path.length > 2 ? 'chain' : 'hop') : 'step',
      hops: Math.max(0, result.path.length - 1),
      finished: result.finished,
    });

    // Keep the visual index aligned with the authoritative engine position.
    // Without this the moved marble keeps its OLD key in `marbles` and in
    // mesh.userData, so the raycaster hands back a stale key, the occupancy
    // lookup misses, and the tap is silently rejected — the marble appears
    // frozen (P0 interaction bug).
    this.reindexMarble(from, to);

    if (result.finished) {
      this.finishMatch(result.winner);
      return;
    }

    this.ctx.hud.setChain(null);
    this.passTurn();
  }

  /** Advance the turn, skipping seat(s) with no legal move. */
  private passTurn(): void {
    this.turns++;
    this.timerLeft = this.match.turnTimerSec;
    let guard = this.game.seats.length;
    while (guard-- > 0 && !this.game.finished) {
      if (this.game.hasAnyMove(this.game.currentPlayer)) break;
      this.game.skipTurn();
      this.turns++;
      this.ctx.hud.toast(tr('noMoves', $settings.get().language));
    }
    this.busy = false;
    this.refreshHud();
    this.beginTurn();
    tutorialEmit('turn-ended'); // v2.1 教程第 4 步推进信号
  }

  private beginTurn(): void {
    if (this.game.finished) return;
    if (this.humanSeats.has(this.game.currentPlayer)) {
      if (this.match.showHints) {
        this.ctx.hud.setHint(tr('selectHint', $settings.get().language));
      }
    } else {
      this.ctx.hud.setHint(null);
      // 联机的非本家座位是远端真人，不走本地 AI。
      if (!this.online) this.scheduleAI();
    }
    this.applyGlow();
  }

  // ── 联机：服务端消息订阅 ──────────────────────────────
  private bindNet(): void {
    this.netOffs.push(
      netClient.on('STATE_UPDATE', (p) => this.enqueueNetState(p)),
      netClient.on('STATE_SYNC', (p) => this.onNetSync(p)),
      netClient.on('GAME_OVER', (p) => this.onNetGameOver(p)),
      netClient.on('PLAYER_PRESENCE', (p) => this.onNetPresence(p)),
      netClient.on('MOVE_REJECTED', (p) => this.onNetRejected(p)),
      netClient.on('ROOM_CLOSED', () => this.onNetRoomClosed())
    );
  }

  /** STATE_UPDATE → 串行动画队列（无预测渲染：等服务端权威帧再动）。 */
  private enqueueNetState(p: StateUpdatePayload): void {
    if (p.state && this.matchId && this.game.finished) return; // 终局后的迟到帧
    this.netQueue.push(p);
    if (!this.netPlaying) this.pumpNetQueue();
  }

  private pumpNetQueue(): void {
    const next = this.netQueue.shift();
    if (!next) {
      this.netPlaying = false;
      // 队列排空后若 GAME_OVER 已在手，统一结算。
      if (this.netGameOver) this.finishOnline();
      return;
    }
    this.netPlaying = true;
    this.replayNetMove(next, () => this.pumpNetQueue());
  }

  /**
   * 重放一步权威走子：按 payload.move.path 播飞行动画，落地后把本地
   * 镜像引擎推进到与快照一致（applyMove + skippedSeats 轮转）。
   */
  private replayNetMove(p: StateUpdatePayload, done: () => void): void {
    const lang = $settings.get().language;
    this.turnDeadlineAt = p.turnDeadlineAt;
    const move = p.move;
    for (const seat of p.skippedSeats) {
      this.ctx.hud.toast(trf('netNoMovesName', lang, { name: this.seatName(seat) }));
    }
    if (move?.auto) {
      this.ctx.hud.toast(
        move.seat === this.yourSeat
          ? tr('netAutoMove', lang)
          : trf('netPeerAuto', lang, { name: this.seatName(move.seat) })
      );
    }
    if (!move) {
      // 纯跳过关照（无人可动）：镜像只做轮转。
      this.mirrorNetState(p);
      done();
      return;
    }

    this.busy = true;
    const fromKey = hexKey(move.from);
    const m = this.marbles.get(fromKey);
    if (!m) {
      // 舞台与权威状态脱节：直接全量对齐，不播动画。
      this.mirrorNetState(p);
      this.busy = false;
      done();
      return;
    }
    const speed = Math.max(0.25, this.match.animationSpeed);
    const path = move.path.length > 0 ? move.path : [move.from, move.to];
    const waypoints: THREE.Vector3[] = [];
    for (const pos of path) {
      const hole = this.board.holeWorld.get(hexKey(pos));
      if (!hole) {
        this.mirrorNetState(p);
        this.busy = false;
        done();
        return;
      }
      waypoints.push(new THREE.Vector3(hole.x, hole.y + MARBLE_RADIUS, hole.z));
    }
    waypoints[0] = m.mesh.position.clone();
    const isHop = hexDistance(move.from, move.to) > 1;
    // v2.1：远端走子与本地同效 —— 路径高亮 + 拖尾 + COMBO 徽章（RULES §5.2）。
    const combo = comboOfPath(path);
    this.showPathFx(path, combo);
    const trail = isHop ? Math.min(1, 0.35 + combo * 0.18) : 0;
    const flySegment = (idx: number): void => {
      const a = waypoints[idx];
      const b = waypoints[idx + 1];
      if (!a || !b) {
        this.onMoveLandedFx(combo);
        this.landNetMove(p, move.from, move.to, isHop);
        this.busy = false;
        done();
        return;
      }
      this.flights.push({
        m,
        from: a,
        to: b,
        t: 0,
        dur: (isHop ? 0.36 : 0.26) / speed,
        arc: isHop ? 1.5 : 0.28,
        trail,
        onDone: () => {
          if (isHop) this.ctx.audio.playLandHop();
          if (idx + 1 >= waypoints.length - 1) {
            m.pos = move.to;
            this.board.shake(isHop ? 1.15 : 0.65);
            if (!isHop) this.ctx.audio.playLandStep();
            this.onMoveLandedFx(combo);
            this.landNetMove(p, move.from, move.to, isHop);
            this.busy = false;
            done();
          } else {
            flySegment(idx + 1);
          }
        },
      });
    };
    if (waypoints.length < 2) {
      this.onMoveLandedFx(combo);
      this.landNetMove(p, move.from, move.to, isHop);
      this.busy = false;
      done();
      return;
    }
    flySegment(0);
  }

  /** 动画落地后的镜像推进（确定性：applyMove 结果与服务端一致）。 */
  private landNetMove(p: StateUpdatePayload, from: HexPos, to: HexPos, isHop: boolean): void {
    try {
      this.game.applyMove(from, to);
    } catch (err) {
      netLogger.warn('镜像引擎拒绝权威走子，改全量对齐', {
        event: 'match.online.mirror_rejected',
        err: toLogErr(err),
      });
      this.mirrorNetState(p);
      return;
    }
    this.moves++;
    this.reindexMarble(from, to);
    // applyMove 已轮转一个座位；被跳过的座位再逐个轮转（skipTurn 零参）。
    p.skippedSeats.forEach(() => this.game.skipTurn());
    logger.debug('联机走子已镜像', {
      event: 'move.applied',
      mode: 'online',
      move: this.moves,
      from: { q: from.q, r: from.r },
      to: { q: to.q, r: to.r },
      kind: isHop ? 'hop' : 'step',
    });
    this.afterNetMirror(p);
  }

  /** 无动画路径的状态对齐（跳过回合 / 脱节恢复）。 */
  private mirrorNetState(p: StateUpdatePayload): void {
    try {
      this.game = CheckersGame.restore(p.state);
    } catch (err) {
      netLogger.warn('STATE_UPDATE 快照无法重建', {
        event: 'match.online.restore_failed',
        err: toLogErr(err),
      });
      return;
    }
    // 引擎整体替换后，舞台弹珠按权威 occupancy 重排。
    this.reconcileStage();
    this.afterNetMirror(p);
  }

  /** 镜像推进后的公共收尾：HUD / 回合提示 / 发光。 */
  private afterNetMirror(p: StateUpdatePayload): void {
    this.turnDeadlineAt = p.turnDeadlineAt;
    this.timerShown = -1; // 新 deadline：倒计时立即重渲染
    this.clearSelection();
    this.refreshHud();
    if (!this.game.finished) this.beginTurn();
  }

  /**
   * 以引擎 occupancy 为权威，把舞台弹珠网格逐个摆回正确孔位
   * （位置不一致的才移动，避免每步都全量重建材质）。
   */
  private reconcileStage(): void {
    const wanted = new Map<string, { seat: number; pos: HexPos }>();
    for (const seat of this.game.seats) {
      for (const pos of this.game.marblesOf(seat)) {
        wanted.set(hexKey(pos), { seat, pos });
      }
    }
    // 移除多余弹珠（键不在权威集合内）。
    for (const [key, m] of [...this.marbles]) {
      if (!wanted.has(key)) {
        this.ctx.engine.getScene().remove(m.mesh);
        m.material.dispose();
        this.marbles.delete(key);
      }
    }
    // 补齐/修正权威弹珠（颜色按座位）。
    const iridescence = this.match.marbleIridescence;
    for (const [key, slot] of wanted) {
      const existing = this.marbles.get(key);
      if (existing && existing.seat === slot.seat) continue;
      if (existing) {
        this.ctx.engine.getScene().remove(existing.mesh);
        existing.material.dispose();
        this.marbles.delete(key);
      }
      const m = createMarble({
        seat: slot.seat,
        pos: slot.pos,
        key,
        color: PLAYER_COLORS[slot.seat] ?? '#E63946',
        iridescence,
      });
      this.placeMarble(m, slot.pos);
      this.ctx.engine.getScene().add(m.mesh);
      this.marbles.set(key, m);
    }
    this.refreshPickables();
  }

  /** 断线重连：以 STATE_SYNC 快照整体重建镜像。 */
  private onNetSync(p: StateSyncPayload): void {
    if (this.matchId && p.matchId !== this.matchId) return; // 再来一局的新房由主流程接管
    this.matchId = p.matchId;
    this.yourSeat = p.yourSeat;
    this.netNames.clear();
    for (const pl of p.players) this.netNames.set(pl.seat, pl.nickname);
    this.netQueue = [];
    this.netPlaying = false;
    this.netGameOver = null;
    try {
      this.game = CheckersGame.restore(p.state);
    } catch (err) {
      netLogger.warn('STATE_SYNC 快照无法重建', {
        event: 'match.online.sync_failed',
        err: toLogErr(err),
      });
      return;
    }
    this.turnDeadlineAt = p.turnDeadlineAt;
    this.timerShown = -1;
    this.reconcileStage();
    this.clearSelection();
    this.refreshHud();
    if (p.phase === 'playing') this.beginTurn();
    netLogger.info('对局已从快照恢复', {
      event: 'match.online.resynced',
      matchId: p.matchId,
      phase: p.phase,
    });
  }

  private onNetGameOver(p: GameOverPayload): void {
    this.netGameOver = p;
    // 动画队列排空中时 pump 会结算；空队列则立即结算。
    if (!this.netPlaying) this.finishOnline();
  }

  /** 终局结算：胜利辉光 + 结果数据 → 延迟切场景。 */
  private finishOnline(): void {
    const over = this.netGameOver;
    if (!over) return;
    this.netGameOver = null;
    const lang = $settings.get().language;
    const winnerSeat = over.winnerSeat;
    if (winnerSeat !== null) {
      this.ctx.audio.playWin();
      this.finishedGlow = {
        keys: new Set(this.game.marblesOf(winnerSeat).map(hexKey)),
        t0: performance.now() / 1000,
      };
    }
    const idx = winnerSeat !== null ? this.game.seats.indexOf(winnerSeat) : -1;
    const winnerName =
      winnerSeat === null
        ? tr('netAborted', lang)
        : winnerSeat === this.yourSeat
          ? tr('you', lang)
          : (this.netNames.get(winnerSeat) ?? `P${idx + 1}`);
    $matchResult.set({
      winnerSeat,
      winnerName,
      winnerColor: winnerSeat !== null ? (PLAYER_COLORS[winnerSeat] ?? '#E63946') : '#E63946',
      seats: [...this.game.seats],
      mode: 'online',
      moves: over.moves,
      rounds: over.rounds,
      yourSeat: this.yourSeat,
      standings: over.standings,
      reason: over.reason,
    });
    this.ctx.hud.setTurn(null);
    this.ctx.hud.setHint(null);
    this.ctx.hud.setChain(null);
    this.finishTimer = setTimeout(() => this.ctx.goTo('result'), FINISH_DELAY_MS);
  }

  /** 对手在场状态变化（断线/重连/认输）→ 轻提示。 */
  private onNetPresence(p: PlayerPresencePayload): void {
    if (p.seat === this.yourSeat) return; // 自己的连接态自知
    const lang = $settings.get().language;
    const name = this.seatName(p.seat);
    const key =
      p.status === 'disconnected'
        ? 'netPeerOffline'
        : p.status === 'resigned'
          ? 'netPeerResigned'
          : 'netPeerBack';
    this.ctx.hud.toast(trf(key, lang, { name }));
  }

  /** 走子被拒：本地无预测，只需提示原因。 */
  private onNetRejected(p: MoveRejectedPayload): void {
    const lang = $settings.get().language;
    this.ctx.hud.toast(
      tr(p.reason === 'NOT_YOUR_TURN' ? 'netNotYourTurn' : 'netMoveRejected', lang)
    );
  }

  /** 房间被关闭（对局中人走茶凉）→ 提示并回主页。 */
  private onNetRoomClosed(): void {
    if (this.match.mode !== 'online') return;
    if (this.game.finished || this.netGameOver) return; // 终局窗口由 ResultScene 处理
    this.ctx.hud.toast(tr('netRoomClosed', $settings.get().language));
    this.ctx.goTo('title');
  }

  // ── AI ──────────────────────────────────────────────────
  /** Resolve the full Target (with hop path) for an engine-level move. */
  private findTarget(from: HexPos, to: HexPos): Target | undefined {
    return this.game.reachableTargets(from).find((t) => t.to.q === to.q && t.to.r === to.r);
  }

  private scheduleAI(): void {
    if (this.aiTimer !== null) clearTimeout(this.aiTimer);
    this.aiTimer = setTimeout(() => {
      this.aiTimer = null;
      if (this.game.finished || this.humanSeats.has(this.game.currentPlayer)) return;
      // v4.0 组队模式：按席位人格（策略×棋力）行棋；其余模式走全局棋力。
      const persona = this.teamPersonas.get(this.game.currentPlayer);
      const mv = chooseAIMove(
        this.game,
        persona?.difficulty ?? this.match.aiDifficulty,
        persona?.strategy ?? 'balanced'
      );
      const target = mv ? this.findTarget(mv.from, mv.to) : undefined;
      if (!mv || !target) {
        this.passTurn();
        return;
      }
      this.executeMove(mv.from, mv.to, target);
    }, AI_DELAY_MS);
  }

  // ── Finish ──────────────────────────────────────────────
  private finishMatch(winnerSeat: number | null): void {
    const seat = winnerSeat ?? this.game.seats[0] ?? 0;
    this.ctx.audio.playWin();
    this.finishedGlow = {
      keys: new Set(this.game.marblesOf(seat).map(hexKey)),
      t0: performance.now() / 1000,
    };
    const lang = $settings.get().language;
    const s = this.match;
    const idx = this.game.seats.indexOf(seat);
    // v4.0 组队：胜者以队伍名义展示（RULES §11.3 团胜判定）。
    const winnerName =
      s.mode === 'ai'
        ? seat === 0
          ? tr('you', lang)
          : tr('ai', lang)
        : s.mode === 'team'
          ? (this.game.winnerTeam ?? 'A') === 'A'
            ? tr('teamWinA', lang)
            : tr('teamWinB', lang)
          : `P${(idx < 0 ? 0 : idx) + 1}`;
    $matchResult.set({
      winnerSeat: seat,
      winnerName,
      winnerColor: PLAYER_COLORS[seat] ?? '#E63946',
      seats: [...this.game.seats],
      mode: s.mode,
      moves: this.moves,
      rounds: Math.max(1, Math.ceil(this.turns / this.game.seats.length)),
      // v2.1：本地局 combo 记帐随结算传递（联机走 standings 的服务端字段）。
      combos: this.game.seats.map((s2) => ({
        seat: s2,
        ...(this.comboStats.get(s2) ?? createComboStats()),
      })),
    });
    this.ctx.hud.setTurn(null);
    this.ctx.hud.setHint(null);
    this.ctx.hud.setChain(null);
    this.finishTimer = setTimeout(() => this.ctx.goTo('result'), FINISH_DELAY_MS);
  }

  // ── HUD ─────────────────────────────────────────────────
  private seatName(seat: number): string {
    const lang = $settings.get().language;
    if (this.match.mode === 'ai') return seat === 0 ? tr('you', lang) : tr('ai', lang);
    const idx = this.game.seats.indexOf(seat);
    if (this.online) {
      if (seat === this.yourSeat) return tr('you', lang);
      return this.netNames.get(seat) ?? `P${(idx < 0 ? 0 : idx) + 1}`;
    }
    // v4.0 组队：人类席位 = 我/玩家n（回合序），AI 席位带人格紧凑标记，
    // 让观众一眼读懂每个 AI 的策略倾向（SCREENS §15.2 人格可读性）。
    if (this.match.mode === 'team') {
      if (this.humanSeats.has(seat)) {
        const ord = this.game.seats.filter((n) => this.humanSeats.has(n)).indexOf(seat) + 1;
        return ord <= 1 ? tr('seatMe', lang) : trf('seatPlayerN', lang, { n: ord });
      }
      const persona = this.teamPersonas.get(seat);
      return persona
        ? `${tr('ai', lang)}·${tr(STRAT_SHORT[persona.strategy], lang)}`
        : tr('ai', lang);
    }
    return `P${(idx < 0 ? 0 : idx) + 1}`;
  }

  /** 回合标题：联机时远端座位是真人，不显示"AI 思考中"。 */
  private turnLabel(seat: number, lang: Language): string {
    if (this.online) {
      return seat === this.yourSeat
        ? tr('yourTurn', lang)
        : `${this.seatName(seat)} · ${tr('seatTurn', lang)}`;
    }
    // 组队单人类席位时与单机一致直述"你的回合"；多人类热座则带座位名。
    const soloHuman =
      this.match.mode === 'ai' || (this.match.mode === 'team' && this.humanSeats.size === 1);
    return this.humanSeats.has(seat)
      ? soloHuman
        ? tr('yourTurn', lang)
        : `${this.seatName(seat)} · ${tr('seatTurn', lang)}`
      : tr('aiThinking', lang);
  }

  /** 联机倒计时文本：deadline（服务端时钟）经 clockOffset 换算到本地。 */
  private netTimerText(lang: Language): string | null {
    if (this.turnDeadlineAt <= 0) return null;
    const left = this.turnDeadlineAt + $net.get().clockOffset - Date.now();
    return `${Math.max(0, Math.ceil(left / 1000))}${tr('secUnit', lang)}`;
  }

  private refreshHud(): void {
    const lang = $settings.get().language;
    const seat = this.game.currentPlayer;
    this.ctx.hud.setTurn({
      color: PLAYER_COLORS[seat] ?? '#E63946',
      label: this.turnLabel(seat, lang),
      timerText: this.online
        ? this.netTimerText(lang)
        : this.match.turnTimerSec > 0
          ? `${Math.max(0, Math.ceil(this.timerLeft))}${tr('secUnit', lang)}`
          : null,
    });
    this.ctx.hud.setPlayers(
      this.game.seats.map((n) => ({
        seat: n,
        color: PLAYER_COLORS[n] ?? '#E63946',
        name: this.seatName(n),
        dest: this.game.marblesInDestination(n),
        active: n === seat && !this.game.finished,
      }))
    );
  }

  // ── Turn timer ──────────────────────────────────────────
  private updateTimer(dt: number): void {
    if (this.online) {
      this.updateNetTimer();
      return;
    }
    if (this.match.turnTimerSec <= 0 || this.game.finished) return;
    this.timerLeft -= dt;
    const sec = Math.max(0, Math.ceil(this.timerLeft));
    if (sec !== this.timerShown) {
      this.timerShown = sec;
      const lang = $settings.get().language;
      const seat = this.game.currentPlayer;
      this.ctx.hud.setTurn({
        color: PLAYER_COLORS[seat] ?? '#E63946',
        label: this.turnLabel(seat, lang),
        timerText: `${sec}${tr('secUnit', lang)}`,
      });
    }
    if (this.timerLeft <= 0) {
      this.timerLeft = this.match.turnTimerSec;
      if (!this.humanTurn()) return; // AI paces itself
      this.ctx.hud.toast(tr('timeoutAuto', $settings.get().language));
      const mv = chooseAIMove(this.game, 'medium');
      const target = mv ? this.findTarget(mv.from, mv.to) : undefined;
      if (!mv || !target) {
        this.passTurn();
        return;
      }
      this.executeMove(mv.from, mv.to, target);
    }
  }

  /**
   * 联机回合倒计时：完全以服务端 deadline 为准（clockOffset 换算），
   * 本地只展示与临期警示 —— 超时托管由服务端权威执行（STATE_UPDATE.auto）。
   */
  private updateNetTimer(): void {
    if (this.game.finished || this.turnDeadlineAt <= 0) return;
    const left = this.turnDeadlineAt + $net.get().clockOffset - Date.now();
    const sec = Math.max(0, Math.ceil(left / 1000));
    if (sec === this.timerShown) return;
    this.timerShown = sec;
    const lang = $settings.get().language;
    const seat = this.game.currentPlayer;
    this.ctx.hud.setTurn({
      color: PLAYER_COLORS[seat] ?? '#E63946',
      label: this.turnLabel(seat, lang),
      timerText: `${sec}${tr('secUnit', lang)}`,
    });
    if (
      left > 0 &&
      left <= TURN_WARNING_MS &&
      seat === this.yourSeat &&
      this.netWarnedFor !== this.turnDeadlineAt
    ) {
      this.netWarnedFor = this.turnDeadlineAt;
      this.ctx.hud.toast(tr('netTurnWarning', lang));
    }
  }

  // ── Visual states ───────────────────────────────────────
  private updateFlights(dt: number): void {
    if (this.flights.length === 0) return;
    const keep: Flight[] = [];
    for (const f of this.flights) {
      f.t = Math.min(1, f.t + dt / f.dur);
      const e = f.t * f.t * (3 - 2 * f.t); // smoothstep
      f.m.mesh.position.lerpVectors(f.from, f.to, e);
      f.m.mesh.position.y += Math.sin(f.t * Math.PI) * f.arc;
      if (f.t >= 1) f.onDone();
      else keep.push(f);
    }
    this.flights = keep;
  }

  // ── v2.1 连跳特效（RULES §8.2/§8.4，本地/远端同效）──────
  /** RULES §5.2：本地局按座位累计 combo（联机由服务端随 standings 下发）。 */
  private trackCombo(seat: number, path: readonly HexPos[]): void {
    const s = this.comboStats.get(seat) ?? createComboStats();
    recordCombo(s, comboOfPath(path));
    this.comboStats.set(seat, s);
  }

  /** 落地公共特效：路径淡出 + COMBO 徽章。 */
  private onMoveLandedFx(combo: number): void {
    this.beginPathFxFade();
    if (combo >= 2) this.showComboBadge(combo);
  }

  /** 走棋期间把权威 path 渲染为黄铜高亮折线 + 各落点脉冲环。 */
  private showPathFx(path: readonly HexPos[], combo: number): void {
    this.clearPathFx();
    if (combo < 1) return; // step 不高亮
    const scene = this.ctx.engine.getScene();
    const pts: THREE.Vector3[] = [];
    for (const p of path) {
      const hole = this.board.holeWorld.get(hexKey(p));
      if (!hole) return; // 棋盘未就绪：放弃特效（动画本体不受影响）
      pts.push(new THREE.Vector3(hole.x, hole.y + 0.14, hole.z));
    }
    if (pts.length < 2) return;
    const brass = new THREE.Color(this.theme.brass);
    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(pts),
      new THREE.LineBasicMaterial({ color: brass, transparent: true, opacity: 0.85 })
    );
    scene.add(line);
    const rings: THREE.Mesh[] = [];
    for (let i = 1; i < pts.length; i++) {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.55, 0.78, 40),
        new THREE.MeshBasicMaterial({
          color: brass,
          transparent: true,
          opacity: 0.8,
          side: THREE.DoubleSide,
          depthWrite: false,
        })
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.copy(pts[i] as THREE.Vector3);
      scene.add(ring);
      rings.push(ring);
    }
    this.pathFx = { line, rings, fadeStart: Number.POSITIVE_INFINITY };
  }

  /** 弹珠落地：折线/脉冲环进入 600ms 淡出。 */
  private beginPathFxFade(): void {
    if (this.pathFx) this.pathFx.fadeStart = performance.now();
  }

  private updatePathFx(elapsed: number): void {
    const fx = this.pathFx;
    if (!fx) return;
    // 常驻期（走棋动画中）：脉冲环呼吸，把视线引到连跳落点。
    const pulse = 1 + Math.sin(elapsed * 7) * 0.14;
    for (const r of fx.rings) r.scale.setScalar(pulse);
    if (!Number.isFinite(fx.fadeStart)) return;
    const k = Math.min(1, (performance.now() - fx.fadeStart) / 600);
    const opacity = (1 - k) * 0.85;
    (fx.line.material as THREE.LineBasicMaterial).opacity = opacity;
    for (const r of fx.rings) (r.material as THREE.MeshBasicMaterial).opacity = opacity;
    if (k >= 1) this.clearPathFx();
  }

  private clearPathFx(): void {
    if (!this.pathFx) return;
    const scene = this.ctx.engine.getScene();
    scene.remove(this.pathFx.line);
    this.pathFx.line.geometry.dispose();
    (this.pathFx.line.material as THREE.Material).dispose();
    for (const r of this.pathFx.rings) {
      scene.remove(r);
      r.geometry.dispose();
      (r.material as THREE.Material).dispose();
    }
    this.pathFx = null;
  }

  /** 运动弹珠拖尾：飞行中每 ~45ms 留一枚渐隐 ghost 残影。 */
  private updateTrailFx(dt: number): void {
    this.ghostAcc += dt;
    const scene = this.ctx.engine.getScene();
    while (this.ghostAcc >= 0.045) {
      this.ghostAcc -= 0.045;
      if (this.ghosts.length >= 120) break; // 上限保护（低端机 / 超长链）
      for (const f of this.flights) {
        if (!f.trail) continue;
        const base = 0.4 * f.trail;
        const mat = new THREE.MeshBasicMaterial({
          color: f.m.material.color,
          transparent: true,
          opacity: base,
          depthWrite: false,
        });
        // 几何体与弹珠共享（只销毁独立材质）。
        const ghost = new THREE.Mesh(f.m.mesh.geometry, mat);
        ghost.position.copy(f.m.mesh.position);
        ghost.scale.copy(f.m.mesh.scale);
        scene.add(ghost);
        const max = 0.3 + 0.12 * f.trail;
        this.ghosts.push({ mesh: ghost, life: max, max, base });
      }
    }
    if (this.ghosts.length === 0) return;
    const keep: TrailGhost[] = [];
    for (const g of this.ghosts) {
      g.life -= dt;
      if (g.life <= 0) {
        scene.remove(g.mesh);
        (g.mesh.material as THREE.Material).dispose();
      } else {
        (g.mesh.material as THREE.MeshBasicMaterial).opacity = g.base * (g.life / g.max);
        keep.push(g);
      }
    }
    this.ghosts = keep;
  }

  private clearTrailFx(): void {
    const scene = this.ctx.engine.getScene();
    for (const g of this.ghosts) {
      scene.remove(g.mesh);
      (g.mesh.material as THREE.Material).dispose();
    }
    this.ghosts = [];
    this.ghostAcc = 0;
  }

  /** COMBO ×N 徽章：单例替换（600ms 弹入 / 800ms 停留 / 400ms 淡出）。 */
  private showComboBadge(combo: number): void {
    if (this.comboBadgeTimer) clearTimeout(this.comboBadgeTimer);
    this.comboBadgeEl?.remove();
    const el = document.createElement('div');
    el.className = 'combo-badge';
    el.textContent = trf('comboBadge', $settings.get().language, { n: combo });
    this.ctx.overlay.appendChild(el);
    this.comboBadgeEl = el;
    this.ctx.audio.playCombo(combo);
    this.comboBadgeTimer = setTimeout(() => {
      if (this.comboBadgeEl === el) this.comboBadgeEl = null;
      this.comboBadgeTimer = null;
      el.remove();
    }, 1800);
  }

  private clearComboBadge(): void {
    if (this.comboBadgeTimer) clearTimeout(this.comboBadgeTimer);
    this.comboBadgeTimer = null;
    this.comboBadgeEl?.remove();
    this.comboBadgeEl = null;
  }

  private applyGlow(): void {
    const seat = this.game.currentPlayer;
    const canPick = this.humanTurn();
    for (const [key, m] of this.marbles) {
      if (this.finishedGlow?.keys.has(key)) continue; // victory glow owns these
      if (key === this.selectedKey) continue; // pulse handled per-frame
      const hoverable = canPick && this.game.occupancy.get(key) === seat;
      m.setEmissive(key === this.hoverKey && hoverable ? 0.16 : 0);
    }
  }

  private updateGlow(elapsed: number): void {
    if (this.selectedKey) {
      const m = this.marbles.get(this.selectedKey);
      m?.setEmissive(0.3 + Math.sin(elapsed * 5.2) * 0.12);
    }
    if (this.finishedGlow) {
      const t = performance.now() / 1000 - this.finishedGlow.t0;
      const pulse = 0.4 + Math.sin(t * 4) * 0.18;
      for (const key of this.finishedGlow.keys) this.marbles.get(key)?.setEmissive(pulse);
    }
  }

  // ── Live settings (语言/提示即时生效，对局参数保持冻结) ──
  private bindLiveSettings(): void {
    this.unsubs.push(
      $settings.subscribe((s) => {
        for (const m of this.marbles.values()) m.material.iridescence = s.marbleIridescence;
        this.match.showHints = s.showHints;
        if (!this.game.finished) {
          this.ctx.hud.setHint(
            s.showHints && this.humanTurn() ? tr('selectHint', s.language) : null
          );
        }
        this.refreshHud();
      })
    );
  }

  // declared late to keep the lifecycle block readable
  private initExtras(): void {
    this.bindLiveSettings();
  }

  /** Called from enter() after first refresh — kept explicit for readability. */
  postEnter(): void {
    this.initExtras();
  }

  // ── v2.1 Tutorial（SCREENS §12）──────────────────────────
  /**
   * 首局自动播放：本地局走完整 6 步（压暗 + 聚光）；联机局只给两条
   * 轻量提示（本营 / 回合计时），不遮 waiting room（dim=false）。
   * 完成或跳过置 tutorialDone；设置面板可"重看教程"重置。
   */
  private maybeStartTutorial(): void {
    if (isTutorialDone()) return;
    const seat = this.online ? this.yourSeat : 0;
    const campRect = (): DOMRect | null => this.campScreenRect(seat);
    const marbleRect = (): DOMRect | null => this.marbleScreenRect(seat);
    const destRect = (): DOMRect | null => this.firstTargetScreenRect();
    const hudRect = (sel: string): DOMRect | null =>
      (document.querySelector(sel) as HTMLElement | null)?.getBoundingClientRect() ?? null;
    const turnRect = (): DOMRect | null => hudRect('.capsule');
    const chainRect = (): DOMRect | null => {
      const el = document.querySelector('.chain-banner') as HTMLElement | null;
      return el && el.style.display !== 'none' ? el.getBoundingClientRect() : turnRect();
    };

    let steps: CoachStep[];
    let dim = true;
    if (this.online) {
      // 联机轻量版（§12.1：两条提示，无压暗）。
      dim = false;
      steps = [
        {
          id: 'camp',
          anchor: campRect,
          titleKey: 'tutCampT',
          bodyKey: 'tutCampB',
          advanceOn: 'next',
        },
        {
          id: 'timer',
          anchor: turnRect,
          titleKey: 'tutTimerT',
          bodyKey: 'tutTimerB',
          advanceOn: 'next',
        },
      ];
    } else {
      steps = [
        {
          id: 'camp',
          anchor: campRect,
          titleKey: 'tutCampT',
          bodyKey: 'tutCampB',
          advanceOn: 'next',
        },
        {
          id: 'select',
          anchor: marbleRect,
          titleKey: 'tutSelectT',
          bodyKey: 'tutSelectB',
          advanceOn: 'marble-selected',
        },
        {
          id: 'dest',
          anchor: destRect,
          titleKey: 'tutDestT',
          bodyKey: 'tutDestB',
          advanceOn: 'destination-tapped',
        },
        {
          id: 'chain',
          anchor: chainRect,
          titleKey: 'tutChainT',
          bodyKey: 'tutChainB',
          advanceOn: 'turn-ended',
        },
        {
          id: 'timer',
          anchor: turnRect,
          titleKey: 'tutTimerT',
          bodyKey: 'tutTimerB',
          advanceOn: 'next',
        },
        {
          id: 'combo',
          anchor: turnRect,
          titleKey: 'tutComboT',
          bodyKey: 'tutComboB',
          advanceOn: 'next',
        },
      ];
    }
    this.coach = new TutorialCoach(this.ctx.overlay, steps, dim);
    this.coach.start();
  }

  /** 本营聚光：营地孔位世界坐标均值的屏幕投影。 */
  private campScreenRect(seat: number): DOMRect | null {
    const holes = CAMP_POSITIONS[seat];
    if (!holes?.length) return null;
    const c = new THREE.Vector3();
    let n = 0;
    for (const p of holes) {
      const w = this.board.holeWorld.get(hexKey(p));
      if (w) {
        c.add(w);
        n++;
      }
    }
    if (n === 0) return null;
    return this.projectRect(c.multiplyScalar(1 / n), 150);
  }

  /** 己方弹珠聚光：第一颗属于 seat 的弹珠网格位置投影。 */
  private marbleScreenRect(seat: number): DOMRect | null {
    for (const [key, m] of this.marbles) {
      if (this.game.occupancy.get(key) === seat) {
        return this.projectRect(m.mesh.position, 46);
      }
    }
    return null;
  }

  /** 落点聚光：当前选中弹珠的第一个合法落点。 */
  private firstTargetScreenRect(): DOMRect | null {
    const tg = this.selectedTargets[0];
    if (!tg) return null;
    const w = this.board.holeWorld.get(hexKey(tg.to));
    return w ? this.projectRect(w, 60) : null;
  }

  /** 世界坐标 → 屏幕 DOMRect（相机可环绕，教练逐帧重投影）。 */
  private projectRect(v: THREE.Vector3, size: number): DOMRect | null {
    const p = v.clone().project(this.ctx.engine.getCamera());
    if (p.z > 1 || p.z < -1) return null; // 视锥外/背后
    const x = (p.x * 0.5 + 0.5) * window.innerWidth;
    const y = (-p.y * 0.5 + 0.5) * window.innerHeight;
    return new DOMRect(x - size / 2, y - size / 2, size, size);
  }
}
